import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { withUrlValidation } from '../components/withUrlValidation';
import ProgressCard from '../components/ProgressCard';
import { useSessionNavigation } from '../hooks/useSessionNavigation';
import { soundManager } from '../utils/soundUtils';
import Bookmark from '../components/Bookmark';
import ExitPopupComponent from '../components/ExitPopup';
import BluePopup from '../components/BluePopup';
import { completeQuiz } from '../utils/quizUtils';
import { findValidUser } from '../utils/firebaseUtils';
import FirebaseErrorPopup from '../components/FirebaseErrorPopup';
import { useFirebaseError } from '../hooks/useFirebaseError';

// 버튼 이미지 import
import exitBtnImg from '../assets/images/quiz-exit.png';
import skipBtnImg from '../assets/images/quiz-skip.png';
import exitPopupImg from '../assets/images/quiz-skip-img.png';
import exitCoinGetImg from '../assets/images/skip-coin-get.png';
import prevBtnImg from '../assets/images/02register-prev-btn.png';
import okBtnImg from '../assets/images/02register-ok-btn.png';

// 스타일드 컴포넌트 추가
const ExitButton = styled.img`
  position: absolute;
  margin-top: 0.5dvh;
  margin-left: 1.5dvw;
  top:2.8dvw;
  left: 2.3dvw;
  width: 20dvw;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 26000;
  /* GPU 레이어 분리 */
  will-change: auto;
  transform: translateZ(0);
  
  &:hover {
    transform: scale(1.05) translateZ(0);
  }
  
  &:active {
    transform: scale(0.95) translateZ(0);
  }
`;

const SkipButton = styled.img`
  position: absolute;
  margin-top: 0.5dvh;
  margin-left: 1.5dvw;
  top:2.8dvw;
  left: 23dvw;
  width: 20dvw;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 26000;
  /* GPU 레이어 분리 */
  will-change: auto;
  transform: translateZ(0);
  
  &:hover {
    transform: scale(1.05) translateZ(0);
  }
  
  &:active {
    transform: scale(0.95) translateZ(0);
  }
`;

const ProgressCardPage: React.FC = () => {
  const { navigateWithSession } = useSessionNavigation();
  const [searchParams] = useSearchParams();
  const fromBookmark = searchParams.get('fromBookmark') === 'true' || localStorage.getItem('fromBookmark') === 'true';
  
  // 상태 변수들 추가
  const [showExitPopup, setShowExitPopup] = useState(false);
  const [showSkipPopup, setShowSkipPopup] = useState(false);
  const [showSkipButtons, setShowSkipButtons] = useState(false);
  const [zoneClearStatus, setZoneClearStatus] = useState<{[key: string]: string}>({});
  const [bookmarkVisible, setBookmarkVisible] = useState(0);
  const [showBookmarkPopup, setShowBookmarkPopup] = useState(false);
  
  // Firebase 에러 핸들링 훅
  const { errorState, handleFirebaseError, hideError } = useFirebaseError();
  
  // 디버깅용 로그 추가
  
  // zoneClearStatus 가져오기
  useEffect(() => {
    const fetchZoneStatus = async () => {
      try {
        const currentUser = localStorage.getItem('currentUser');
        if (!currentUser) {
          return;
        }
        
        const userData = JSON.parse(currentUser);
        
        // 로컬 스토리지에서 직접 zoneClearStatus 가져오기
        if (userData.zoneClearStatus) {
          setZoneClearStatus(userData.zoneClearStatus);
        } else {
          // Firebase에서도 시도해보기
          const firebaseUserData = await findValidUser(userData.nickname, userData.avatarNumber);
          
          if (firebaseUserData && firebaseUserData.zoneClearStatus) {
            setZoneClearStatus(firebaseUserData.zoneClearStatus);
          } else {
          }
        }
      } catch (error) {
        console.error('Error fetching zone status:', error);
        // Firebase 에러 팝업 표시
        handleFirebaseError(error, '사용자 정보를 불러오는 중 오류가 발생했습니다.');
      }
    };

    fetchZoneStatus();
  }, []); // 컴포넌트 마운트 시에만 실행

  // 북마크 표시 타이머
  useEffect(() => {
    const bookmarkTimer = setTimeout(() => setBookmarkVisible(1), 700);
    return () => clearTimeout(bookmarkTimer);
  }, []);

  // 페이지를 떠날 때 localStorage 상태 초기화
  useEffect(() => {
    return () => {
      localStorage.removeItem('fromBookmark');
    };
  }, []);

  // 사용자 데이터 업데이트 함수 (스킵 기능용)
  const updateUserData = async (isSuccess: boolean, zoneNumber: number): Promise<boolean> => {
    try {
      const currentUser = localStorage.getItem('currentUser');
      if (!currentUser) {
        console.error('로그인된 사용자 정보가 없습니다.');
        alert('로그인 정보를 찾을 수 없습니다. 다시 로그인해주세요.');
        return false;
      }

      const userData = JSON.parse(currentUser);
      
      // 이미 퀴즈를 풀었는지 확인 (로컬 스토리지에서)
      const currentZoneStatus = userData.zoneClearStatus?.[`zone${zoneNumber}`] || '아직안품';
      if (currentZoneStatus !== '아직안품') {
        return true; // 이미 풀었으면 성공으로 처리하되 업데이트는 하지 않음
      }

      // 새로운 퀴즈 완료 처리 함수 사용
      const result = await completeQuiz(
        zoneNumber,
        isSuccess,
        userData.nickname,
        userData.avatarNumber,
        userData.coinCount || 0
      );

      if (!result.success) {
        console.error('퀴즈 완료 처리에 실패했습니다.');
        alert('데이터 저장 중 오류가 발생했습니다. 다시 시도해주세요.');
        return false;
      }

      
      return true;
    } catch (error) {
      console.error('사용자 데이터 업데이트 중 오류:', error);
      alert('데이터 저장 중 오류가 발생했습니다. 다시 시도해주세요.');
      return false;
    }
  };

  // 종료 버튼 핸들러
  const handleExitButtonClick = () => {
    // 북마크에서 ProgressCard가 열려있을 때 이벤트 발생
    window.dispatchEvent(new CustomEvent('quizExit'));
    
    // EXIT 팝업 표시
    setShowExitPopup(true);
  };

  // 스킵 버튼 핸들러
  const handleSkipButtonClick = () => {
    // 북마크에서 ProgressCard가 열려있을 때 이벤트 발생
    window.dispatchEvent(new CustomEvent('quizSkip'));
    
    // SKIP 팝업 표시
    setShowSkipPopup(true);
    setShowSkipButtons(false); // 버튼 초기화
    
    // 1.5초 후에 버튼들 표시
    setTimeout(() => {
      setShowSkipButtons(true);
    }, 1500);
  };

  // EXIT 팝업 관련 핸들러들
  const handleExitPopupClose = () => {
    setShowExitPopup(false);
  };

  const handleExitConfirmed = () => {
    setShowExitPopup(false);
    // ExitPopup 컴포넌트 내부에서 처리됨
  };

  // SKIP 팝업 관련 핸들러들
  const handleSkipPopupClose = () => {
    setShowSkipPopup(false);
    setShowSkipButtons(false);
  };

  const handleSkipConfirmed = async () => {
    setShowSkipPopup(false);
    setShowSkipButtons(false);
    
    // 현재 진행 상태에 따라 다음 퀴즈를 스킵
    const nextQuizZone = getNextQuizZone();
    if (nextQuizZone > 0) {
      const updateSuccess = await updateUserData(false, nextQuizZone);
      if (updateSuccess) {
        // 현재 URL의 파라미터를 그대로 유지하면서 새로고침
        const currentUrl = window.location.href;
        window.location.href = currentUrl;
      }
    }
  };

  const handleCardClick = (cardIndex: number) => {
    // 카드 인덱스에 따라 해당 퀴즈 페이지로 이동
    switch (cardIndex) {
      case 0: // card1
        navigateWithSession('/quiz1');
        break;
      case 1: // card2
        navigateWithSession('/quiz2');
        break;
      case 2: // card3
        navigateWithSession('/quiz3');
        break;
      case 3: // card4
        navigateWithSession('/quiz4');
        break;
      case 4: // card5
        navigateWithSession('/quiz5');
        break;
      case 5: // card6
        navigateWithSession('/quiz6');
        break;
      default:
        break;
    }
  };

  // 다음에 풀어야 할 퀴즈 zone 찾기
  const getNextQuizZone = () => {
    for (let i = 1; i <= 6; i++) {
      const zoneStatus = zoneClearStatus[`zone${i}`];
      if (zoneStatus === '아직안품') {
        return i;
      }
    }
    return 0; // 모든 퀴즈를 다 풀었을 때
  };

  // 스킵할 퀴즈의 인물 이름 매핑
  const getQuizCharacterName = (zoneNumber: number) => {
    const characterNames = {
      1: '이지혜',
      2: '이현성', 
      3: '정희원',
      4: '유상아',
      5: '한수영',
      6: '신유승'
    };
    return characterNames[zoneNumber as keyof typeof characterNames] || '알 수 없음';
  };

  // 스킵 버튼 표시 조건: 아직 안 푼 퀴즈가 있을 때
  const shouldShowSkipButton = getNextQuizZone() > 0;
  
  // 종료하기 버튼 표시 조건: 아직 안 푼 퀴즈가 있을 때 (스킵 버튼과 동일)
  const shouldShowExitButton = getNextQuizZone() > 0;
  
  // 디버깅용 로그 추가

  return (
    <>
      <ProgressCard onCardClick={handleCardClick} fromBookmark={fromBookmark} />
      
      {/* 북마크 아이콘 */}
      <Bookmark 
        visible={bookmarkVisible} 
        zIndex={25000}
        showPopup={showBookmarkPopup}
        onPopupChange={setShowBookmarkPopup}
        disableProgress={true}
      />
      
      {/* 종료 버튼 - 아직 안 푼 퀴즈가 있을 때만 표시 */}
      {shouldShowExitButton && (
        <ExitButton
          src={exitBtnImg}
          alt="나가기"
          onClick={handleExitButtonClick}
        />
      )}
      
      {/* 스킵 버튼 - 아직 안 푼 퀴즈가 있을 때만 표시 */}
      {shouldShowSkipButton && (
        <SkipButton
          src={skipBtnImg}
          alt="건너뛰기"
          onClick={handleSkipButtonClick}
        />
      )}
      
      {/* EXIT 팝업 컴포넌트 */}
      <ExitPopupComponent
        visible={showExitPopup}
        onClose={handleExitPopupClose}
        onExit={handleExitConfirmed}
        exitPopupImage={exitPopupImg}
        coinGetImage={exitCoinGetImg}
      />
      
      {/* SKIP 팝업 */}
      {showSkipPopup && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100dvw',
          height: '100dvh',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 17000
        }}>
          <div style={{
            width: '100dvw',
            position: 'relative'
          }}>
            <BluePopup
              onClose={handleSkipPopupClose}
            >
              <div style={{ 
                fontSize: '6dvw', 
                marginBottom: '2dvh'
              }}>
                '{getQuizCharacterName(getNextQuizZone())}' 인물퀴즈를{'\n'}스킵하시겠습니까?
              </div>
              <div style={{ 
                fontSize: '3.8dvw'
              }}>
                스킵하면 실패보상과 함께{'\n'}다음 인물퀴즈로 넘어갑니다.
              </div>
            </BluePopup>
            {showSkipButtons && (
              <div style={{ 
                display: 'flex', 
                justifyContent: 'center', 
                gap: '3dvw',
                position: 'absolute',
                bottom: '-30%',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '80%',
                zIndex: 1001,
                opacity: showSkipButtons ? 1 : 0,
                transition: 'opacity 0.3s ease'
              }}>
                <img
                  src={prevBtnImg}
                  alt="이전"
                  style={{ 
                    width: '40dvw', 
                    height: 'auto',
                    cursor: 'pointer'
                  }}
                  onClick={handleSkipPopupClose}
                />
                <img
                  src={okBtnImg}
                  alt="네"
                  style={{ 
                    width: '40dvw', 
                    height: 'auto',
                    cursor: 'pointer'
                  }}
                  onClick={handleSkipConfirmed}
                />
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* Firebase 에러 팝업 */}
      <FirebaseErrorPopup
        isVisible={errorState.isError}
        onClose={hideError}
        message={errorState.errorMessage}
      />
    </>
  );
};

export default withUrlValidation(ProgressCardPage, {
  requireValidation: true,
  showCountdown: true
}); 